// src/components/admin/TDSManagement.js
import React, { useState, useEffect } from 'react';
import {
    Receipt, Download, Users, TrendingUp, FileText,
    Search, Calendar, Settings, Shield, DollarSign
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';

const TDSManagement = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('OVERVIEW');
    const [employees, setEmployees] = useState([]);
    const [tdsReports, setTdsReports] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('2024-11');
    const [selectedFY, setSelectedFY] = useState('2024-25');
    const [loading, setLoading] = useState(false);
    const [tdsEnabled, setTdsEnabled] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);

    const tabs = ['OVERVIEW', 'EMPLOYEE TDS', 'EXEMPTIONS', 'REPORTS', 'CONFIGURATION'];
    const financialYears = ['2024-25', '2023-24', '2022-23', '2021-22'];

    // Fetch all employees TDS data
    const fetchEmployeesTDS = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/employees');
            if (response.data.success) {
                setEmployees(response.data.employees || []);
            }
        } catch (err) {
            console.error('Error fetching employees TDS data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch TDS reports
    const fetchTDSReports = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/tds/reports/monthly', {
                params: { month: selectedMonth }
            });
            if (response.data.success) {
                setTdsReports(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching TDS reports:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch TDS configuration
    const fetchTDSConfig = async () => {
        try {
            const response = await api.get('/api/payroll/config/tds');
            if (response.data.success) {
                setTdsEnabled(response.data.config?.tds_enabled ?? true);
            }
        } catch (err) {
            console.error('Error fetching TDS config:', err);
            // Default to enabled if fetch fails
            setTdsEnabled(true);
        }
    };

    // Save TDS configuration
    const saveTDSConfig = async () => {
        setSavingConfig(true);
        try {
            const response = await api.post('/api/payroll/config/tds', {
                tds_enabled: tdsEnabled
            });
            if (response.data.success) {
                alert('TDS configuration saved successfully!');
            }
        } catch (err) {
            console.error('Error saving TDS config:', err);
            alert('Failed to save TDS configuration. Please try again.');
        } finally {
            setSavingConfig(false);
        }
    };

    useEffect(() => {
        fetchEmployeesTDS();
        fetchTDSConfig();
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${parseFloat(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-700/30'
                        : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                }`}>
                    <Users className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Total Employees
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {employees.length}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <Receipt className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-red-400' : 'text-red-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Total TDS (This Month)
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {formatCurrency(tdsReports?.total_tds || 0)}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <DollarSign className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Total Taxable Income
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {formatCurrency(tdsReports?.total_taxable_income || 0)}
                    </p>
                </div>

                <div className={`p-6 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <Shield className={`w-8 h-8 mb-3 ${
                        theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                    }`} />
                    <h3 className={`text-sm font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        Exempted Employees
                    </h3>
                    <p className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        {tdsReports?.exempted_count || 0}
                    </p>
                </div>
            </div>

            {/* Quick Actions */}
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => setActiveTab('REPORTS')}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <Download className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Generate Form 16
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                For FY {selectedFY}
                            </p>
                        </div>
                    </button>

                    <button
                        onClick={() => setActiveTab('EXEMPTIONS')}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <FileText className="w-5 h-5 text-green-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Manage Exemptions
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Review requests
                            </p>
                        </div>
                    </button>

                    <button
                        onClick={() => setActiveTab('REPORTS')}
                        className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                            theme === 'dark'
                                ? 'border-gray-700 hover:bg-gray-700/50'
                                : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <TrendingUp className="w-5 h-5 text-purple-500" />
                        <div className="text-left">
                            <p className={`font-medium ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                View Reports
                            </p>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Monthly & annual
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderEmployeeTDS = () => {
        const filteredEmployees = employees.filter(emp =>
            emp.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            emp.employee_code?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <div className="space-y-4">
                {/* Search Bar */}
                <div className={`flex items-center gap-4 p-4 rounded-lg border ${
                    theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                    <div className="flex-1 relative">
                        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`} />
                        <input
                            type="text"
                            placeholder="Search by name or employee code..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                            }`}
                        />
                    </div>
                    <select
                        value={selectedFY}
                        onChange={(e) => setSelectedFY(e.target.value)}
                        className={`px-4 py-2 rounded-lg border ${
                            theme === 'dark'
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    >
                        {financialYears.map(fy => (
                            <option key={fy} value={fy}>{fy}</option>
                        ))}
                    </select>
                </div>

                {/* Employee TDS Table */}
                <div className={`overflow-x-auto rounded-lg border ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <table className="w-full">
                        <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                            <tr>
                                <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Employee</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Gross Salary (Annual)</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Taxable Income</th>
                                <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>TDS Deducted (YTD)</th>
                                <th className={`px-4 py-3 text-center text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Tax Regime</th>
                                <th className={`px-4 py-3 text-center text-xs font-semibold ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Status</th>
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${
                            theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
                        }`}>
                            {filteredEmployees.map((employee) => (
                                <tr key={employee.id} className={
                                    theme === 'dark' ? 'bg-gray-800/50 hover:bg-gray-700/50' : 'bg-white hover:bg-gray-50'
                                }>
                                    <td className={`px-4 py-3 ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        <div>
                                            <p className="font-medium">
                                                {employee.first_name} {employee.last_name}
                                            </p>
                                            <p className={`text-sm ${
                                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                {employee.employee_code}
                                            </p>
                                        </div>
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-right ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        {formatCurrency(employee.tds_data?.gross_salary || 0)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-right ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        {formatCurrency(employee.tds_data?.taxable_income || 0)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                                        theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                    }`}>
                                        {formatCurrency(employee.tds_data?.ytd_tds || 0)}
                                    </td>
                                    <td className={`px-4 py-3 text-sm text-center ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                        {employee.tax_regime || 'New'}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            employee.tds_exempted
                                                ? theme === 'dark'
                                                    ? 'bg-green-900/30 text-green-400'
                                                    : 'bg-green-100 text-green-700'
                                                : theme === 'dark'
                                                    ? 'bg-blue-900/30 text-blue-400'
                                                    : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {employee.tds_exempted ? 'Exempted' : 'Active'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderExemptions = () => (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    TDS Exemption Requests
                </h3>

                <div className={`p-8 text-center rounded-lg border ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <Shield className={`w-12 h-12 mx-auto mb-3 ${
                        theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                    }`} />
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        No pending exemption requests
                    </p>
                </div>
            </div>
        </div>
    );

    const renderReports = () => (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    Generate TDS Reports
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Form 16 */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <FileText className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Form 16 - Bulk Generation
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Generate Form 16 for all employees
                        </p>
                        <button className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                            Generate All Form 16
                        </button>
                    </div>

                    {/* Monthly TDS Report */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <Receipt className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Monthly TDS Report
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Detailed month-wise TDS deduction report
                        </p>
                        <button className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
                            Generate Report
                        </button>
                    </div>

                    {/* Quarterly TDS Return */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <Download className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Quarterly TDS Return (Form 24Q)
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Generate quarterly return for filing
                        </p>
                        <button className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                            Download Return
                        </button>
                    </div>

                    {/* Annual Tax Summary */}
                    <div className={`p-4 rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <TrendingUp className={`w-8 h-8 mb-3 ${
                            theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                        }`} />
                        <h4 className={`font-semibold mb-2 ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Annual Tax Summary
                        </h4>
                        <p className={`text-sm mb-4 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Year-end tax summary for all employees
                        </p>
                        <button className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors">
                            Generate Summary
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderConfiguration = () => (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold mb-6 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    TDS Configuration
                </h3>

                {/* TDS Enable/Disable Toggle */}
                <div className={`p-4 rounded-lg border mb-6 ${
                    theme === 'dark' ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h4 className={`font-semibold mb-1 ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Enable Tax Deducted at Source (TDS)
                            </h4>
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                {tdsEnabled
                                    ? 'TDS deductions are currently enabled for all eligible employees'
                                    : 'TDS deductions are currently disabled. Enable this to automatically calculate income tax deductions'
                                }
                            </p>
                        </div>
                        <button
                            onClick={() => setTdsEnabled(!tdsEnabled)}
                            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                tdsEnabled
                                    ? 'bg-blue-600 focus:ring-blue-500'
                                    : theme === 'dark'
                                        ? 'bg-gray-700 focus:ring-gray-600'
                                        : 'bg-gray-300 focus:ring-gray-400'
                            }`}
                        >
                            <span
                                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                    tdsEnabled ? 'translate-x-7' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>
                </div>

                {/* Save Configuration Button */}
                <div className="mb-6">
                    <button
                        onClick={saveTDSConfig}
                        disabled={savingConfig}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                            savingConfig
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </button>
                </div>

                {/* Tax Slabs Information (New Regime FY 2024-25) */}
                <div className="mb-6">
                    <h4 className={`text-sm font-semibold mb-3 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                        Tax Slabs - New Regime (FY 2024-25)
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>₹0 - ₹3,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                                }`}>0%</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>₹3,00,001 - ₹6,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                                }`}>5%</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>₹6,00,001 - ₹9,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
                                }`}>10%</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>₹9,00,001 - ₹12,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
                                }`}>15%</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>₹12,00,001 - ₹15,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                }`}>20%</span>
                            </div>
                        </div>
                        <div className={`p-3 rounded-lg border ${
                            theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                            <div className="flex justify-between">
                                <span className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>Above ₹15,00,000</span>
                                <span className={`text-sm font-semibold ${
                                    theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                }`}>30%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Health & Education Cess
                        </label>
                        <input
                            type="text"
                            defaultValue="4%"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Rebate u/s 87A (Income {'<='} ₹7,00,000)
                        </label>
                        <input
                            type="text"
                            defaultValue="₹25,000"
                            disabled
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-gray-400'
                                    : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}
                        />
                    </div>
                </div>

                <div className={`mt-6 p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
                }`}>
                    <p className={`text-sm ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                    }`}>
                        <strong>Note:</strong> Tax slabs and rates are as per Income Tax Act 1961 (New Regime for FY 2024-25).
                        TDS calculation includes 4% Health & Education Cess and applicable rebates.
                        Employees can declare exemptions through tax declaration portal.
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div className={`tour-comp-tds min-h-screen p-6 ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className={`text-2xl font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        TDS Management
                    </h1>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Manage employee TDS, exemptions, and generate tax reports
                    </p>
                </div>

                {/* Tabs */}
                <div className={`border-b mb-6 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex gap-1 overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                                    activeTab === tab
                                        ? theme === 'dark'
                                            ? 'text-blue-400 border-b-2 border-blue-400'
                                            : 'text-blue-600 border-b-2 border-blue-600'
                                        : theme === 'dark'
                                            ? 'text-gray-400 hover:text-gray-300'
                                            : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'OVERVIEW' && renderOverview()}
                    {activeTab === 'EMPLOYEE TDS' && renderEmployeeTDS()}
                    {activeTab === 'EXEMPTIONS' && renderExemptions()}
                    {activeTab === 'REPORTS' && renderReports()}
                    {activeTab === 'CONFIGURATION' && renderConfiguration()}
                </div>
            </div>
        </div>
    );
};

export default TDSManagement;
